export class PipeFunctions {

    static readonly oneKilo = 1024;
    static readonly oneMega = PipeFunctions.oneKilo * PipeFunctions.oneKilo;

    /**
     * Liefert aus einem deutschen Datum das Jahr ("01.02.2020" -> "2020").
     * Ohne Punkt bzw. bei leerem Wert wird die Eingabe unveraendert zurueckgegeben.
     *
     * @param raw Datum im deutschen Format (z.B. "TT.MM.JJJJ")
     * @returns Das Jahr als String, oder die unveraenderte Eingabe
     */
    static pickYearOfDeDate(raw: string): string {
        if (!raw) {
            return raw;
        }

        const index = raw.lastIndexOf('.');

        if (index !== -1) {
            return raw.substring(index + 1, raw.length);
        } else {
            return raw;
        }
    }

    /**
     * Auftraggeber werden manchmal im Format "Firma/öffentl. AG         Beispiel GmbH" übertragen
     * Splitte bei Bedarf auf und hole den korrekten Auftraggebernamen raus
     *
     * @param raw  Der Auftraggebername wie er aus Zebra kommt
     */
    static pickCustomer(raw: string): string {
        if (!raw) {
            return raw;
        }

        const index = raw.lastIndexOf('   ');

        // Es wird nur ein Feld übertragen, kein Split nötig gebe Wert komplett zurück
        if (index === -1) {
            return raw.trim();
        }

        if (   raw.startsWith('Firma/öffentl. AG')
            || raw.startsWith('Firma/öffentl. Auftraggeber')
            || raw.startsWith('Privatperson')
        ) {
            // Der richtige Auftraggebername steht hinten, übertrage nur den hinteren Teil
            return raw.substring(index + 3, raw.length).trim();
        } else {
            // Der richtige Auftraggebername steht vorne, übertrage nur den vorderen Teil
            return raw.substring(0, index).trim();
        }
    }

    /**
     * Bereitet eine VSNR fuer die Anzeige auf, indem der Unternummern-Teil ab dem
     * letzten Slash abgeschnitten wird. Ein URL-kodierter Slash ("%2F") wird zuvor
     * in "/" zurueckgewandelt. Beispiel: "5-981-202040/0000" -> "5-981-202040".
     *
     * @param raw VSNR, ggf. mit Unternummer und/oder URL-Kodierung
     * @returns VSNR ohne Unternummern-Teil, oder die unveraenderte Eingabe
     */
    static formatVsnr(raw: string): string {
        if (!raw) {
            return raw;
        }

        const tmp = raw.replace('%2F', '/');
        const index = tmp.lastIndexOf('/');

        if (index !== -1) {
            return tmp.substring(0, index);
        } else {
            return tmp;
        }
    }

    /**
     * Wie {@link formatVsnr}, extrahiert aber zusaetzlich strikt das Kernmuster
     * "Geschaeftsstelle-Stammnummer" (z.B. "981-202040") per Regex. Passt das Muster
     * nicht, wird das Ergebnis von {@link formatVsnr} zurueckgegeben.
     *
     * @param raw VSNR in beliebigem Format
     * @returns Das Kernmuster "GG-Stammnummer", sonst das formatVsnr-Ergebnis
     */
    static formatVsnrRigid(raw: string): string {
        if (!raw) {
            return raw;
        }

        const result = PipeFunctions.formatVsnr(raw);
        const rx = /[0-9]{2,3}-[0-9]{6}/;
        const foo = rx.exec(result);

        return foo && foo.length === 1 ? foo[0] : result;
    }

    /**
     * Formatiert eine Byte-Anzahl als Kilobyte-Angabe mit einer Nachkommastelle und
     * deutschem Dezimalkomma (z.B. 1536 -> "1,5"). Basiert auf 1 KB = 1024 Bytes.
     *
     * @param bytes Anzahl Bytes
     * @returns Kilobyte-Angabe im Format "G,N"
     */
    static bytesToKilo(bytes: number): string {
        return PipeFunctions.bytesToT(bytes, PipeFunctions.oneKilo);
    }

    /**
     * Formatiert eine Byte-Anzahl als Megabyte-Angabe mit einer Nachkommastelle und
     * deutschem Dezimalkomma (z.B. 1572864 -> "1,5"). Basiert auf 1 MB = 1024*1024 Bytes.
     *
     * @param bytes Anzahl Bytes
     * @returns Megabyte-Angabe im Format "G,N"
     */
    static bytesToMega(bytes: number): string {
        return PipeFunctions.bytesToT(bytes, PipeFunctions.oneMega);
    }

    /**
     * Formatiert eine Byte-Anzahl mit der passenden Einheit (B, KB, MB, GB, ...) und
     * rundet auf ganze Zahlen (z.B. 1048576 -> "1 MB"). Waehlt die groesste Einheit,
     * bei der der Wert unter 1024 bleibt. Negative Werte werden unterstuetzt.
     *
     * @param bytes Anzahl Bytes
     * @returns Menschenlesbare Groessenangabe inkl. Einheit
     */
    static bytesToHumanReadable(bytes: number): string {
        // Basierend auf https://stackoverflow.com/a/14919494

        const thresh = 1024;
        const decimalPrecision = 0;

        if (Math.abs(bytes) < thresh) {
            return bytes + ' B';
        }

        const units = ['KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
        let u = -1;
        const r = 10 ** decimalPrecision;

        do {
            bytes /= thresh;
            ++u;
        } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);

        return bytes.toFixed(decimalPrecision) + ' ' + units[u];
    }

    /**
     * Hilfsfunktion: teilt die Bytes durch den Teiler `t`, rundet kaufmaennisch auf
     * eine Nachkommastelle auf und formatiert das Ergebnis mit deutschem Dezimalkomma.
     *
     * @param bytes Anzahl Bytes
     * @param t     Teiler (z.B. 1024 fuer KB, 1024*1024 fuer MB)
     * @returns Wert im Format "G,N"
     */
    private static bytesToT(bytes: number, t: number): string {
        const f = Math.ceil(bytes / t * 10);
        if (f < 10) {
            return '0,' + f;
        } else {
            return Math.floor(f / 10) + ',' + f % 10;
        }
    }

    /**
     * Normalisiert eine Buergscheinnummer auf ihre volle Darstellung mit fuehrenden
     * Nullen im Format "NNN-JJ/AA" (laufende Nummer 3-stellig, Geschaeftsjahr 2-stellig,
     * Ausfertigung 2-stellig). Beispiel: "1-15/2" -> "001-15/02".
     *
     * @param raw Buergscheinnummer, z.B. "1-15/2" oder "045-15/01"
     * @returns Buergscheinnummer mit fuehrenden Nullen, oder die unveraenderte Eingabe
     */
    static formatBuergscheinNummer(raw: string): string {
        if (!raw) {
            return raw;
        }

        const arr: string[] = raw.split(/[\/-]/);
        let result: string = PipeFunctions.setZeroPrefix(arr[0], 3);
        result += '-';
        result += PipeFunctions.setZeroPrefix(arr[1], 2);
        result += '/';
        result += PipeFunctions.setZeroPrefix(arr[2], 2);

        return result;
    }

    /**
     * Hilfsfunktion: fuellt einen String links mit Nullen auf die Ziellaenge auf.
     * Ist der String bereits laenger oder gleich lang, bleibt er unveraendert.
     *
     * @param raw          Auszufuellender Wert
     * @param targetLength Gewuenschte Gesamtlaenge
     * @returns Der links mit Nullen aufgefuellte String
     */
    private static setZeroPrefix(raw: string, targetLength: number): string {
        const numZeros = targetLength - raw.length;
        let stringBuilder: string = '';

        for (let i = 0; i < numZeros; i++) {
            stringBuilder += '0';
        }

        return stringBuilder + raw;
    }

    /**
     * Bereitet einen Prozentwert fuer die Anzeige auf: ersetzt den Dezimalpunkt durch
     * ein deutsches Komma und haengt " %" an. Beispiel: "12.5" -> "12,5 %".
     *
     * @param raw Prozentwert als String (Dezimaltrennung per Punkt)
     * @returns Formatierter Prozentwert, oder die unveraenderte Eingabe
     */
    static formatPercent(raw: string): string {
        if (!raw) {
            return raw;
        }

        const tmp = (raw + '').replace('.', ',');

        return tmp + ' %';
    }

    /**
     * Wandelt eine VSNR in beliebigem Format in einen OB13 (Ordnungsbegriff 13-stellig) um.
     *
     * Akzeptierte Eingabeformate (Beispiele für ein und dieselbe VSNR):
     *  - "5-981-202040/0000"   (Format 1, Vollauspraegung)
     *  - "5812020400000"       (Format 2, OB13 - wird unverändert zurückgegeben)
     *  - "B 981-202040"        (Format 3, Kundenformat)
     *  - "B-981-202040"        (Format 3, Variante)
     *  - "981-202040"          (Format 4, ohne B-Präfix)
     *  - "81-202040"           (Format 4, ohne Prüfziffer)
     *
     * Ergebnis im Beispiel jeweils: "5812020400000"
     *
     * @param raw VSNR in beliebigem Format
     * @returns Die VSNR im OB13-Format ("5" + Geschäftsstelle(2) + Stammnummer(6) + Unternummer(4)),
     *          oder der unveränderte Eingabewert, wenn keine Umwandlung möglich ist.
     */
    static vsnrToNumerisch(raw: string): string {
        if (!raw) {
            return raw;
        }

        // Whitespace und URL-encoded Slash entfernen/ersetzen
        const trimmed = raw.replace(/\s+/g, '').replace('%2F', '/');

        // Bereits OB13? (13 Ziffern, beginnt mit 5)
        if (/^5\d{12}$/.test(trimmed)) {
            return trimmed;
        }

        // Muster "[Prüfziffer]Geschäftsstelle-Stammnummer[/Unternummer]"
        // Prüfziffer (1 Ziffer) optional, Geschäftsstelle (2 Ziffern), Stammnummer (6 Ziffern),
        // Unternummer (1-4 Ziffern) optional.
        // Davor darf ein beliebiges Präfix stehen (z.B. "B", "B-", "5-").
        const match = /(?:^|[^0-9])(\d?)(\d{2})-(\d{6})(?:\/(\d{1,4}))?$/.exec(trimmed);

        if (!match) {
            return raw;
        }

        const geschaeftsstelle = match[2];
        const stammnummer = match[3];
        const unternummer = PipeFunctions.setZeroPrefix(match[4] ?? '', 4);

        return '5' + geschaeftsstelle + stammnummer + unternummer;
    }

}
